import * as React from "react";
import type { RefetchOptions, UseQueryResult } from "@tanstack/react-query";
import { EmptyState } from "./empty-state";
import { ErrorState, type StateVariant } from "./error-state";

/**
 * `refetch` is only invoked; its result is ignored. A wider return type lets
 * both `useQuery` (`ListData<T>`) and `useInfiniteQuery` (`InfiniteData<...>`)
 * satisfy the prop.
 */
type AsyncQuery<T> = Omit<
  Pick<UseQueryResult<T>, "data" | "isPending" | "isError" | "error" | "refetch">,
  "refetch"
> & {
  refetch: (options?: RefetchOptions) => Promise<unknown>;
};

interface AsyncBoundaryProps<T> {
  query: AsyncQuery<T>;
  skeleton: React.ReactNode;
  /** Returns true when `data` should be treated as empty (defaults to array-length checks). */
  isEmpty?: (data: T) => boolean;
  empty: React.ReactNode;
  errorTitle?: string;
  /** Visual style of the built-in error state — `immersive` on the public site. */
  variant?: StateVariant;
  render: (data: T) => React.ReactNode;
}

function defaultIsEmpty(data: unknown): boolean {
  if (Array.isArray(data)) return data.length === 0;
  if (data && typeof data === "object" && "list" in data) {
    return (
      Array.isArray((data as { list: unknown[] }).list) &&
      (data as { list: unknown[] }).list.length === 0
    );
  }
  return data == null;
}

/**
 * The project's single decision point for the loading -> error -> empty ->
 * content state machine (skill section 9.1). Every list/detail view that
 * fetches data renders through this rather than scattering `if (isLoading)`
 * checks — the skeleton must mirror the real layout, never a bare spinner.
 */
export function AsyncBoundary<T>({
  query,
  skeleton,
  isEmpty = defaultIsEmpty,
  empty,
  errorTitle,
  variant,
  render,
}: AsyncBoundaryProps<T>) {
  if (query.isPending) return <>{skeleton}</>;

  if (query.isError) {
    const message =
      query.error instanceof Error
        ? query.error.message
        : typeof query.error === "object" && query.error !== null && "message" in query.error
          ? String((query.error as { message: unknown }).message)
          : undefined;
    return (
      <ErrorState
        title={errorTitle}
        description={message}
        variant={variant}
        onRetry={() => query.refetch()}
      />
    );
  }

  if (query.data === undefined || isEmpty(query.data)) return <>{empty}</>;

  return <>{render(query.data)}</>;
}
